import { CheckIcon, XIcon } from 'lucide-react'
import type {
  LawQuestionnaireQuestion,
  ProductLawKey,
} from '@/shared/hooks/api/product'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  contributionBadgeVariant,
  contributionCardClassName,
  type LawQuestionnaireContribution,
} from '../utils/lawApplicabilityQuestionnaire'
import {
  localizeLawQuestionnaireQuestionHelper,
  localizeLawQuestionnaireQuestionLabel,
  useLawQuestionnaireTranslation,
} from '../utils/localizeLawQuestionnaireQuestion'

export function V2LawApplicabilityQuestionnaireFieldset({
  law,
  question,
  currentAnswer,
  contribution,
  disabled,
  isPending,
  allowUnset = true,
  onSelectAnswer,
}: {
  law: ProductLawKey
  question: LawQuestionnaireQuestion
  currentAnswer: boolean | null
  contribution: LawQuestionnaireContribution
  disabled: boolean
  isPending: boolean
  allowUnset?: boolean
  onSelectAnswer: (questionKey: string, value: boolean | null) => void
}) {
  const { t, i18n } = useLawQuestionnaireTranslation(law)
  const label = localizeLawQuestionnaireQuestionLabel(
    t,
    law,
    question,
    i18n.language
  )
  const helper = localizeLawQuestionnaireQuestionHelper(
    t,
    law,
    question,
    i18n.language
  )
  const showHelper = helper != null && helper.length > 0
  const showRationale =
    question.rationale != null && question.rationale.length > 0
  const isDimmed = contribution === 'unused'
  const titleId = `law-question-${question.questionKey}`
  const isYesSelected = currentAnswer === true
  const isNoSelected = currentAnswer === false
  const controlsDisabled = disabled || isPending

  function handleAnswerClick(value: boolean) {
    if (allowUnset && currentAnswer === value) {
      onSelectAnswer(question.questionKey, null)
      return
    }
    onSelectAnswer(question.questionKey, value)
  }

  return (
    <div
      role='group'
      aria-labelledby={titleId}
      className={cn(
        'flex items-start gap-3 rounded-md border p-3 last:mb-0',
        contributionCardClassName(contribution),
        isDimmed && 'opacity-60'
      )}
    >
      <div className='min-w-0 flex-1 space-y-2'>
        <Badge variant={contributionBadgeVariant(contribution)}>
          {t(`v2.applicability.questionnaire.contributions.${contribution}`)}
        </Badge>
        <div className='space-y-0.5'>
          <div className='flex flex-wrap items-center gap-2 text-sm leading-relaxed'>
            <p id={titleId} className='font-serif font-bold'>
              {label}
            </p>
          </div>
          {showHelper && (
            <p className='font-serif text-xs leading-relaxed text-muted-foreground'>
              {helper}
            </p>
          )}
        </div>

        <hr />

        {showRationale && (
          <p className='text-sm leading-relaxed text-muted-foreground'>
            <span className='font-medium text-foreground/80'>
              {t('v2.applicability.questionnaire.discoverRationaleLabel')}
            </span>{' '}
            {question.rationale}
          </p>
        )}
      </div>
      <div className='flex shrink-0 gap-1'>
        <Button
          type='button'
          size='icon-xs'
          variant='outline'
          disabled={controlsDisabled}
          aria-pressed={isYesSelected}
          aria-label={t('v2.applicability.questionnaire.answers.yes')}
          className={cn(
            'text-green-600 hover:bg-green-50 hover:text-green-700 dark:text-green-500 dark:hover:bg-green-950 dark:hover:text-green-400',
            isYesSelected &&
              'border-green-600 bg-green-50 text-green-700 dark:border-green-500 dark:bg-green-950 dark:text-green-400'
          )}
          onClick={() => {
            handleAnswerClick(true)
          }}
        >
          <CheckIcon />
        </Button>
        <Button
          type='button'
          size='icon-xs'
          variant='outline'
          disabled={controlsDisabled}
          aria-pressed={isNoSelected}
          aria-label={t('v2.applicability.questionnaire.answers.no')}
          className={cn(
            'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-500 dark:hover:bg-red-950 dark:hover:text-red-400',
            isNoSelected &&
              'border-red-600 bg-red-50 text-red-700 dark:border-red-500 dark:bg-red-950 dark:text-red-400'
          )}
          onClick={() => {
            handleAnswerClick(false)
          }}
        >
          <XIcon />
        </Button>
      </div>
    </div>
  )
}
